import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { beneficiarySectionValues } from 'features/tissInvoicing/containers/sadt/GuideSADT/BeneficiarySection/validationSchema';
import { expensesSectionValues } from 'features/tissInvoicing/containers/sadt/GuideSADT/ExpenseSection/validationSchema';
import { generalSectionValues } from 'features/tissInvoicing/containers/sadt/GuideSADT/GeneralSection/validationSchema';
import { headerSectionValues } from 'features/tissInvoicing/containers/sadt/GuideSADT/HeaderSection/validationSchema';
import { proceduresSectionValues } from 'features/tissInvoicing/containers/sadt/GuideSADT/ProceduresSection/validationSchema';
import { sessionsSectionValues } from 'features/tissInvoicing/containers/sadt/GuideSADT/SessionsSection/validationSchema';
import { solicitorSectionValues } from 'features/tissInvoicing/containers/sadt/GuideSADT/SolicitorSection/validationSchema';
import {
  ActionKeys,
  Autocomplete,
  DomainTables,
  GuideSADT,
  GuideSADTForm,
  HiredSolicitantCodeEntry,
  HiredSolicitantSlug,
  SnackbarProps,
} from 'features/tissInvoicing/types';
import getSnackbarPayload from 'features/tissInvoicing/utils/getSnackbarPayload';
import { normalizeGuideSADTResponse } from './normalizers';

export type SadtFormState = {
  guide?: GuideSADT;
  autocomplete: Autocomplete;
  domainTables: DomainTables;
  form: {
    data: GuideSADTForm;
    expensesErrors: boolean;
  };
  print: {
    pdfURL: string;
    openModal: boolean;
  };
  loading: {
    [key in ActionKeys]: boolean;
  };
  snackbar: SnackbarProps;
};

const name = 'sadt';

export const initialState: SadtFormState = {
  guide: {} as GuideSADT,
  domainTables: {
    councils: [{ id: 0, term: 'Carregando', code: '', term_short: '' }],
    states: [{ id: 0, term: 'Carregando', code: '', uf: '' }],
    reasons_termination: [{ id: 0, term: 'Carregando', code: '' }],
    service_types: [{ id: 0, term: 'Carregando', code: '' }],
    appointment_types: [{ id: 0, term: 'Carregando', code: '' }],
    degrees_participation: [{ id: 0, term: 'Carregando', code: '' }],
    access_ways: [{ id: 0, term: 'Carregando', code: '' }],
    techniques_used: [{ id: 0, term: 'Carregando', code: '' }],
    service_character: [{ id: 0, term: 'Carregando', code: '' }],
    profession_code: [{ id: 0, term: 'Carregando', code: '' }],
    accident_indication: [{ id: 0, term: 'Carregando', code: '' }],
    special_coverages: [{ id: 0, term: 'Carregando', code: '1' }],
    attendance_regimen: [{ id: 0, term: 'Carregando', code: '' }],
    occupational_health: [{ id: 0, term: 'Carregando', code: '' }],
  },
  autocomplete: {
    physicians: [],
    clinics: [],
    procedures: [],
    hired_solicitants: [],
    versions: [],
  },
  form: {
    data: {
      ...headerSectionValues,
      ...generalSectionValues,
      ...beneficiarySectionValues,
      ...solicitorSectionValues,
      ...proceduresSectionValues,
      ...sessionsSectionValues,
      ...expensesSectionValues,
    },
    expensesErrors: false,
  },
  print: {
    pdfURL: '',
    openModal: false,
  },
  loading: {
    fetchGuide: true,
    fetchDomainTables: true,
    fetchAutocomplete: true,
    save: false,
    print: false,
  },
  snackbar: getSnackbarPayload(),
};

const fetchGuideData = createAction<{ eventId: string }>(
  `${name}/fetchGuideData`,
);
const fetchDomainTables = createAction(`${name}/fetchDomainTables`);
const fetchAutocompleteOptions = createAction<{ eventId: string }>(
  `${name}/fetchAutocompleteOptions`,
);
const fetchHiredSolicitantData = createAction<{
  eventId: string;
  solicitantId: number;
  type: HiredSolicitantSlug;
  callback?: ({ code_type, code }: HiredSolicitantCodeEntry) => void;
}>(`${name}/fetchHiredSolicitantData`);

const saveGuide = createAction<{ guideId: string; data: GuideSADTForm }>(
  `${name}/saveGuide`,
);
const printGuide = createAction<{
  guideId: string;
  eventId: string;
  data: GuideSADTForm;
}>(`${name}/printGuide`);

const sadtFormSlice = createSlice({
  name,
  initialState,
  reducers: {
    setLoadingStatus: (
      state,
      action: PayloadAction<{ key: ActionKeys; value: boolean }>,
    ) => ({
      ...state,
      loading: {
        ...state.loading,
        [action.payload.key]: action.payload.value,
      },
    }),
    setDomainTables: (state, action: PayloadAction<DomainTables>) => ({
      ...state,
      domainTables: action.payload,
    }),
    setAutocompleteOptions: (
      state,
      action: PayloadAction<Omit<Autocomplete, 'hired_solicitants'>>,
    ) => ({
      ...state,
      autocomplete: { ...state.autocomplete, ...action.payload },
    }),
    setHiredSolicitants: (
      state,
      action: PayloadAction<HiredSolicitantCodeEntry>,
    ) => ({
      ...state,
      autocomplete: {
        ...state.autocomplete,
        hired_solicitants: [
          ...state.autocomplete.hired_solicitants,
          action.payload,
        ],
      },
    }),
    setGuideData: (state, action: PayloadAction<GuideSADT>) => ({
      ...state,
      guide: action.payload,
      form: {
        ...state.form,
        data: normalizeGuideSADTResponse(action.payload),
      },
    }),
    setGuidePDFUrl: (state, action) => ({
      ...state,
      print: {
        ...state.print,
        pdfURL: action.payload,
      },
    }),
    setPrintModalOpen: (state, action) => ({
      ...state,
      print: {
        ...state.print,
        openModal: action.payload,
      },
    }),
    setSnackbarContent: (state, action) => ({
      ...state,
      snackbar: {
        message: action.payload.message || null,
        severity: action.payload.severity || 'default',
      },
    }),
    setExpensesErrors: (state, action: PayloadAction<boolean>) => ({
      ...state,
      form: {
        ...state.form,
        expensesErrors: action.payload,
      },
    }),
  },
});

export default sadtFormSlice.reducer;
export const actions = {
  ...sadtFormSlice.actions,
  fetchGuideData,
  fetchDomainTables,
  fetchAutocompleteOptions,
  fetchHiredSolicitantData,
  saveGuide,
  printGuide,
};
